/**
 * CompileGateGroupRail - Left panel listing groups with counts.
 * Clicking a group filters the DataGrid to that group; "All" clears the filter.
 */

import React from 'react';
import { cn } from '@/lib/utils';

export interface GroupRailItem {
  key: string;
  label: string;
  count: number;
}

interface CompileGateGroupRailProps {
  groups: GroupRailItem[];
  selectedKey: string | null;
  onSelect: (key: string | null) => void;
  compactMode: boolean;
}

export const CompileGateGroupRail: React.FC<CompileGateGroupRailProps> = ({
  groups,
  selectedKey,
  onSelect,
  compactMode,
}) => {
  const textClass = compactMode ? 'text-xs' : 'text-sm';
  const paddingClass = compactMode ? 'px-2 py-1.5' : 'px-3 py-2';

  return (
    <div
      className="flex flex-col border-r border-nb-border-subtle bg-nb-surface-2 shrink-0 w-52 overflow-y-auto"
      role="navigation"
      aria-label="Groups"
    >
      <button
        type="button"
        onClick={() => onSelect(null)}
        className={cn(
          'text-left border-b border-nb-border-subtle transition-colors',
          paddingClass,
          textClass,
          selectedKey === null
            ? 'bg-nb-accent/15 text-nb-accent font-medium'
            : 'text-nb-text-primary hover:bg-nb-surface-1'
        )}
      >
        All
      </button>
      {groups.map(({ key, label, count }) => {
        const isSelected = selectedKey === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelect(key)}
            className={cn(
              'text-left transition-colors flex items-center justify-between gap-2',
              paddingClass,
              textClass,
              isSelected
                ? 'bg-nb-accent/15 text-nb-accent font-medium'
                : 'text-nb-text-primary hover:bg-nb-surface-1'
            )}
          >
            <span className="truncate flex-1 min-w-0">{label}</span>
            <span className="shrink-0 text-nb-text-muted tabular-nums">{count}</span>
          </button>
        );
      })}
    </div>
  );
};
